import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, IconButton, SvgIcon, Tooltip } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { getYupSchemaFromMetaData } from '../../../../../utils/yupSchema/yupSchemaGenerator';
import FormRenderer from '../../../../../framework/Inputs/formRenderer/formRenderer';
import './addCommodityDetails.scss';
import InputButton from '../../../../../framework/Inputs/inputButton/inputButton';
import { GET } from '../../../../../api/axios';
import { ReactComponent as InfoIcon } from '../../../../../assets/images/InfoIcon.svg';
import { dynamicSort } from '../../../../../utils/helperFunctions';
import { ServiceEndPoints } from '../../../../../utils/constants/ApiConstant';
import { documentCode } from '../../../../../utils/constants/constants';
import { MISC_VALUES } from '../../../../../utils/constants/constants';

const AddCommodityDetails = (props) => {
  const {
    addNewCommodityDetails,
    updateCommodityDetails,
    marketPlacesList,
    isMarketplacesCommodity,
    addressType,
    isEU,
    isAddressBook,
    data: { operationType, rowDetails, index, totalWeight },
  } = props;

  const countryDetails = { ...(rowDetails || {}).countryOfOrigin };
  const formField = getFormField(totalWeight);
  const normalizedAddressType = addressType?.toLowerCase();
  const showProductIdentifiers = !isAddressBook && normalizedAddressType === MISC_VALUES.RESIDENTIAL_ADDRESS_TYPE && isEU;
  const showDdsReferenceNumber = !isAddressBook &&
    [MISC_VALUES.RESIDENTIAL_ADDRESS_TYPE, MISC_VALUES.BUSINESS_ADDRESS_TYPE].includes(normalizedAddressType) && isEU;

  const validationSchema = getYupSchemaFromMetaData(formField, [], [], [['quantity', 'weight']]);
  const emptyValues = {
    commodityCode: '',
    fullDescriptionOfGoods: '',
    weight: '',
    quantity: '',
    unitValue: '',
    countryOfOrigin: '',
    skuNumber: '',
    marketplaceChannel: '',
    merchant: '',
    nonStandardised: '',
    standardised: '',
    ddsReferenceNumber: '',
    documentCode: '',
  };

  const [countries, setCountries] = useState([]);
  const [disblebtn, setdisblebtn] = useState(false);
  const [initialValues, setInitialValues] = useState(
    operationType === 'updateCommodityDetails' ? rowDetails : emptyValues,
  );

  const formikRef = useRef(null);

  const isFormValid = (errors) => {
    if (!isMarketplacesCommodity) {
      delete errors.skuNumber;
      delete errors.marketplaceChannel;
    }
    if (!showProductIdentifiers) {
      delete errors.merchant;
      delete errors.nonStandardised;
      delete errors.standardised;
    }
    if (!showDdsReferenceNumber) {
      delete errors.ddsReferenceNumber;
      delete errors.documentCode;
    }
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (values, actions) => {
    if (operationType === 'updateCommodityDetails') {
      updateCommodityDetails(index, values);
    } else {
      addNewCommodityDetails(values);
    }
  };

  const getCountry = (countryCode) => {
    return countries.find((country) => country.code === countryCode);
  };

  useEffect(() => {
    if (operationType === 'updateCommodityDetails') {
      let existingValues = { ...rowDetails };
      existingValues['countryOfOrigin'] = getCountry((countryDetails || {}).code);
      setInitialValues(existingValues);
    } else {
      emptyValues['countryOfOrigin'] = getCountry('GBR');
      setInitialValues(emptyValues);
    }
  }, [countries, operationType, rowDetails]);

  useEffect(() => {
    // eslint-disable-next-line new-cap
    GET(ServiceEndPoints.getCountries)
      .then((res) => {
        setCountries(res.data.countries.sort(dynamicSort('name')));
      })
      .catch((err) => {
        setCountries([]);
      });
  }, []);

  const resetbtnForm = async (values) => {
    await formikRef.current.setTouched(
      Object.fromEntries(Object.keys(formField).map((fieldName) => [fieldName, true])),
      false,
    );
    const errors = await formikRef.current.validateForm(values);
    if (isFormValid(errors)) {
      await setdisblebtn(true);
      handleSubmit(values);
    } else {
    }
  };
  const resetCommodityForm = (resetForm, setFieldValue) => {
    resetForm();
    setFieldValue('countryOfOrigin', getCountry('GBR'));
  };

  return (
    <div className="wd-100">
      <Grid container>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnMount={true}
          innerRef={formikRef}
        >
          {({ values, setFieldValue, resetForm, dirty }) => (
            <Form className="wd-100">
              <Grid container spacing={2} direction="column" style={{ paddingTop: '12px' }}>
                {marketPlacesList && (
                  <>
                    <Grid item>
                      <FormRenderer {...formField.skuNumber.props} fullWidth />
                    </Grid>
                    <Grid item>
                      <FormRenderer {...formField.marketplaceChannel.props} data={marketPlacesList} fullWidth />
                    </Grid>{' '}
                  </>
                )}
                <Grid item>
                  <Grid container spacing={1} direction="row" justify="center" alignItems="center">
                    <Grid item xs={11} sm={11}>
                      <FormRenderer {...formField.commodityCode.props} fullWidth />
                    </Grid>
                    <Grid item xs={1} sm={1}>
                      <Tooltip title="Use the info button to look this up at gov.uk">
                        <IconButton target="_blank" href="https://www.trade-tariff.service.gov.uk/sections">
                          <SvgIcon fontSize="large" component={InfoIcon} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <FormRenderer {...formField.fullDescriptionOfGoods.props} fullWidth />
                </Grid>
                <Grid item>
                  <FormRenderer {...formField.weight.props} fullWidth />
                </Grid>
                <Grid item>
                  <FormRenderer {...formField.quantity.props} fullWidth />
                </Grid>
                <Grid item>
                  <FormRenderer {...formField.unitValue.props} fullWidth />
                </Grid>
                <Grid item>
                  <FormRenderer {...formField.countryOfOrigin.props} data={countries} fullWidth />
                </Grid>
                {showProductIdentifiers && (
                      <>
                        <Grid item>
                          <Grid container spacing={1} direction="row" justify="center" alignItems="center">
                            <Grid item xs={11} sm={11}>
                              <FormRenderer {...formField.merchant.props} fullWidth />
                            </Grid>
                            <Grid item xs={1} sm={1}>
                              <Tooltip title="Your own unique reference used to identify the product, such as SKU or internal catalogue code.">
                                  <SvgIcon fontSize="large" component={InfoIcon} />
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Grid container spacing={1} direction="row" justify="center" alignItems="center">
                            <Grid item xs={11} sm={11}>
                              <FormRenderer {...formField.nonStandardised.props} fullWidth />
                            </Grid>
                            <Grid item xs={1} sm={1}>
                              <Tooltip title="A product identifier assigned by the manufacturer, such as their own model number, or production reference, that is not recognised internationally.">
                                  <SvgIcon fontSize="large" component={InfoIcon} />
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Grid container spacing={1} direction="row" justify="center" alignItems="center">
                            <Grid item xs={11} sm={11}>
                              <FormRenderer {...formField.standardised.props} fullWidth />
                            </Grid>
                            <Grid item xs={1} sm={1}>
                              <Tooltip title="A product identifier assigned by the manufacturer that is recognised internationally, such as a GTIN, EAN, UPC or ISBN. To be supplied only where it exists.">
                                  <SvgIcon fontSize="large" component={InfoIcon} />
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                )}
                {showDdsReferenceNumber && (
                  <>
                    <Grid item>
                      <Grid container spacing={1} direction="row" justify="center" alignItems="center">
                        <Grid item xs={11} sm={11}>
                          <FormRenderer {...formField.ddsReferenceNumber.props} fullWidth />
                        </Grid>
                        <Grid item xs={1} sm={1}>
                          <Tooltip title="Enter the Due Diligence Statement reference for your goods that are in scope of EUDR (EU deforestation)">
                              <SvgIcon fontSize="large" component={InfoIcon} />
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Grid>
                  <Grid item>
                     <Grid container spacing={1} direction="row" justify="center" alignItems="center">
                        <Grid item xs={11} sm={11}>
                          <FormRenderer {...formField.documentCode.props} data={documentCode} fullWidth />
                        </Grid>
                        <Grid item xs={1} sm={1}>
                          <Tooltip title="Select the EUDR document code to confirm compliance with EU deforestation rules or indicate if your goods are exempt">
                              <SvgIcon fontSize="large" component={InfoIcon} />
                          </Tooltip>
                        </Grid>
                      </Grid>
                  </Grid>
                  </>
                )}
                <Grid item>
                  <Grid spacing={3} container direction="row" justify="flex-end" alignItems="center">
                    <Grid item>
                      <InputButton
                        variant="outlined"
                        className="outlined-btn"
                        onClick={() => resetCommodityForm(resetForm, setFieldValue)}
                        label="Reset"
                        buttonType="reset"
                        isDisabled={!dirty}
                      />
                    </Grid>
                    <Grid item>
                      <InputButton
                        variant="contained"
                        className="contained-btn"
                        buttonType="button"
                        label={operationType === 'updateCommodityDetails' ? 'Update' : 'Add'}
                        isDisabled={disblebtn}
                        onClick={() => resetbtnForm(values)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    </div>
  );
};

AddCommodityDetails.propTypes = {};

export default AddCommodityDetails;

const getFormField = (totalWeight) => {
  return {
    commodityCode: {
      props: {
        name: 'commodityCode',
        label: 'Commodity code',
        type: 'text',
        class_Name: 'commodity_Code',
        inputProps: {
          maxLength: 8,
        },
        isRequired: true,
      },
      validation: {
        validationType: 'string',
        validations: [
          {
            type: 'required',
            params: ['Commodity code is required'],
          },
          {
            type: 'matches',
            params: [/^[0-9]{8}$/, 'Commodity code should be a 8 digit number'],
          },
          {
            type: 'max',
            params: [8, 'Commodity code should be a 8 digit number'],
          },
          {
            type: 'min',
            params: [8, 'Commodity code should be a 8 digit number'],
          },
          {
            type: 'test',
            params: [
              'isCommodityCodeValid',
              'Commodity code is invalid',
              (value) => {
                if (value?.length === 8) {
                  return new Promise((resolve, reject) => {
                    GET(`${ServiceEndPoints.commodityCode}?code=${value}`)
                      .then((res) => {
                        resolve(true);
                      })
                      .catch((err) => {
                        resolve(false);
                      });
                  });
                }
              },
            ],
          },
        ],
      },
    },
    fullDescriptionOfGoods: {
      props: {
        name: 'fullDescriptionOfGoods',
        label: 'Description of goods',
        type: 'text',
        isRequired: true,
        inputProps: {
          maxLength: 50,
        },
      },
      validation: {
        validationType: 'string',
        validations: [
          {
            type: 'max',
            params: [50, 'Maximum of 50  characters are allowed'],
          },
          {
            type: 'required',
            params: ['Description of goods is required'],
          },
          {
            type: 'min',
            params: [1, 'Minimum 1 character is required'],
          },
          {
            type: 'matches',
            params: [
              /(.*[a-zA-Z0-9$&+,:;=?@#|'<>.^*()%!-]){3}/i,
              'Description must be at least 3 non blank characters',
            ],
          },
        ],
      },
    },
    weight: {
      props: {
        name: 'weight',
        label: 'Net weight (kg)',
        type: 'number',
        isRequired: true,
        inputProps: {
          min: 0,
          max: 999,
          step: 'any',
        },
      },
      validation: {
        validationType: 'number',
        validations: [
          {
            type: 'required',
            params: ['Weight is required'],
          },
          {
            type: 'min',
            params: [0.01, 'Weight must be greater than 0'],
          },
          {
            type: 'max',
            params: [999, 'Weight must be 999 or less'],
          },
        ],
      },
    },
    quantity: {
      props: {
        name: 'quantity',
        label: 'Quantity',
        type: 'number',
        isRequired: true,
        inputProps: {
          max: 999,
          min: 1,
        },
      },
      validation: {
        validationType: 'number',
        name: 'quantity',
        validations: [
          {
            type: 'required',
            params: ['Quantity is required'],
          },
          {
            type: 'min',
            params: [1, 'Quantity must be greater than or equals to 1'],
          },
          {
            type: 'max',
            params: [999, 'Quantity must be 999 or less'],
          },
          {
            type: 'integer',
            params: ['Quantity should be a whole number'],
          },
        ],
      },
    },
    unitValue: {
      props: {
        name: 'unitValue',
        label: 'Unit value',
        type: 'number',
        isRequired: true,
        inputProps: {
          min: 0,
          max: 1000000,
          step: 'any',
        },
      },
      validation: {
        validationType: 'number',
        name: 'unitValue',
        validations: [
          {
            type: 'required',
            params: ['Unit value is required'],
          },
          {
            type: 'max',
            params: [10000, 'Unit value must be 10000 or less'],
          },
          {
            type: 'min',
            params: [0.01, 'Unit value must be greater than 0'],
          },
        ],
      },
    },
    countryOfOrigin: {
      props: {
        name: 'countryOfOrigin',
        label: 'Country of origin',
        type: 'select',
        displayField: 'name',
        isRequired: true,
      },
      validation: {
        validationType: 'object',
        name: 'countryOfOrigin',
        validations: [
          {
            type: 'required',
            params: ['Country of origin is required'],
          },

          {
            type: 'max',
            params: [2, 'Maximum of 2 characters allowed'],
          },
          {
            type: 'min',
            params: [2, 'Minimum of 2 characters allowed'],
          },
        ],
      },
    },

    skuNumber: {
      props: {
        name: 'skuNumber',
        label: 'SKU number',
        type: 'text',
        isRequired: true,
        hasDefaultValue: false,
      },
      validation: {
        validationType: 'string',
        name: 'skuNumber',
        validations: [
          {
            type: 'required',
            params: ['SKU number is required'],
          },
        ],
      },
    },
    marketplaceChannel: {
      props: {
        name: 'marketplaceChannel',
        label: 'Marketplace channel',
        type: 'select',
        displayField: 'marketplaceName',
        isRequired: true,
        hasDefaultValue: true,
      },
      validation: {
        validationType: 'object',
        name: 'marketplaceChannel',
        validations: [
          {
            type: 'required',
            params: ['Marketplace channel is required'],
          },
        ],
      },
    },
    merchant: {
      props: {
        name: 'merchant',
        label: 'Merchant Product ID',
        type: 'text',
        class_Name: 'merchant',
        inputProps: {
          maxLength: 70,
        },
        isRequired: true,
      },
      validation: {
        validationType: 'string',
        validations: [
          {
            type: 'required',
            params: ['Merchant Product ID is required'],
          },
          {
            type: 'matches',
            params: [/\S/, { message: 'Merchant Product ID is required', excludeEmptyString: true }],
          },
          {
            type: 'max',
            params: [70, 'Merchant Product ID should not exceed 70 characters'],
          },
          {
            type: 'min',
            params: [1, 'Merchant Product ID should be at least 1 character'],
          },
          {
            type: 'matches',
            params: [/^[^<>]+$/, 'Merchant Product ID must be alphanumeric and cannot contain < and >.'],
          },
        ],
      },
    },
    nonStandardised: {
      props: {
        name: 'nonStandardised',
        label: 'Non-Standardised Manufacturer Product ID',
        type: 'text',
        class_Name: 'nonStandardised',
        inputProps: {
          maxLength: 70,
        },
        isRequired: true,
      },
      validation: {
        validationType: 'string',
        validations: [
          {
            type: 'required',
            params: ['Non-Standardised Manufacturer Product ID is required'],
          },
          {
            type: 'matches',
            params: [
              /\S/,
              { message: 'Non-Standardised Manufacturer Product ID is required', excludeEmptyString: true },
            ],
          },
          {
            type: 'max',
            params: [70, 'Non-Standardised Manufacturer Product ID should not exceed 70 characters'],
          },
          {
            type: 'min',
            params: [1, 'Non-Standardised Manufacturer Product ID should be at least 1 character'],
          },
          {
            type: 'matches',
            params: [/^[^<>]+$/, 'Non-Standardised Manufacturer Product ID must be alphanumeric and cannot contain < and >.'],
          },
        ],
      },
    },
    standardised: {
      props: {
        name: 'standardised',
        label: 'Standardised Manufacturer Product ID',
        type: 'text',
        class_Name: 'standardised',
        inputProps: {
          maxLength: 70,
        },
        isRequired: false,
      },
      validation: {
        validationType: 'string',
        validations: [
          {
            type: 'max',
            params: [70, 'Standardised Manufacturer Product ID should not exceed 70 characters'],
          },
          {
            type: 'min',
            params: [1, 'Standardised Manufacturer Product ID should be at least 1 character'],
          },
          {
            type: 'matches',
            params: [/^[^<>]+$/, 'Standardised Manufacturer Product ID must be alphanumeric and cannot contain < and >.'],
          },
        ],
      },
    },
    ddsReferenceNumber: {
      props: {
        name: 'ddsReferenceNumber',
        label: 'DDS Reference',
        type: 'text',
        class_Name: 'ddsReferenceNumber',
        inputProps: {
          maxLength: 14,
        },
        isRequired: false,
      },
      validation: {
        validationType: 'string',
        validations: [
          {
            type: 'max',
            params: [14, 'DDS Reference should not exceed 14 characters'],
          },
          {
            type: 'min',
            params: [1, 'DDS Reference should be at least 1 character'],
          },
          {
            type: 'matches',
            params: [/^[^<>]+$/, 'DDS Reference must be alphanumeric and cannot contain < and >.'],
          },
        ],
      },
    },
    documentCode: {
      props: {
        name: 'documentCode',
        label: 'EUDR Document Code',
        type: 'select',
        displayField: 'name',
        wrapOptions: true,
        isRequired: false,
      },
      validation: {
        validationType: 'object',
        name: 'documentCode',
        validations: [
          {
            type: 'max',
            params: [2, 'Maximum of 2 characters allowed'],
          },
          {
            type: 'min',
            params: [2, 'Minimum of 2 characters allowed'],
          },
        ],
      },
    },
  };
};
